import { ReactNode } from "react";
import { BehaviorSubject } from "rxjs";
import { noPropagateDialog$ } from "src/store/global.store";


const interval$ = new BehaviorSubject<NodeJS.Timeout | null>(null);
const counter$ = new BehaviorSubject<number>(0);

export const percent$ = new BehaviorSubject<number | null>(null);
export const openNotifier$ = new BehaviorSubject<boolean>(false);
export const showProgress$ = new BehaviorSubject<boolean>(false);

export const notifier$ = new BehaviorSubject<INotifier>({
    title: "",
    content: "",
    timeOut: 6000,
    onClose: () => { },
    noTimeOut: false,
})

export interface INotifier {
    title: ReactNode;
    content: string | string[] | ReactNode;
    onClose?: Function;
    timeOut?: number;
    noTimeOut?: boolean;
    onFinishTimeout?: () => void;
}

export interface INotifierController {
    setNotifier(notifier: INotifier): void;
    getnotifier(): INotifier;
    setOpen(open: boolean): void;
    open(): void;
    getOpen(): boolean;
    getTitle(): ReactNode;
    getContent(): string | string[] | ReactNode;
    getPercent(): number | null;
    setPercent(percent: number): void;
    getShowProgress(): boolean;
}

class NotifierStore implements INotifierController {
    constructor() {
        openNotifier$.next(false);
        showProgress$.next(false);
        interval$.next(null);
        counter$.next(0);
        percent$.next(0);
    }

    public setNotifier(notifier: INotifier): void {
        if (noPropagateDialog$.getValue()) return;
        notifier$.next(notifier);
    }

    public getnotifier(): INotifier {
        return notifier$.getValue();
    }

    public setOpen(open: boolean): void {
        openNotifier$.next(open);
        if (notifier$.getValue().noTimeOut) {
            this.stopTimer();
            return;
        }
        this.startTimeOut();
    }
    
    public open(): void {
        if (noPropagateDialog$.getValue()) return;
        openNotifier$.next(true);
        if (notifier$.getValue().noTimeOut) {
            this.stopTimer();
            return;
        }
        this.startTimeOut();
    }

    public getOpen(): boolean {
        return openNotifier$.getValue();
    }

    public getTitle(): ReactNode {
        return notifier$.getValue().title;
    }

    public getContent(): string | string[] | ReactNode {
        return notifier$.getValue().content;
    }

    // Variável de controle, para evitar loop infinito caso o onClose do notifier chame o método `close` novamente.
    private isClosing = false;

    public close(): void {
        if (this.isClosing) return;

        this.isClosing = true;
        try {
            notifier$.getValue().onClose?.();
        } catch (error) {
            console.error("[NotifierStore] Erro ao executar onClose do notifier:", error);
        } finally {
            this.isClosing = false;
        }

        this.setOpen(false);
        this.stopTimer();
    }

    private reset(): void {
        notifier$.next({
            title: "",
            content: "",
            timeOut: 6000,
            onClose: () => { },
            noTimeOut: false,
        });
        showProgress$.next(false);
        interval$.next(null);
        counter$.next(0);
        percent$.next(0);
    }

    public getPercent(): number | null {
        return percent$.getValue();
    }

    setPercent(percent: number): void {
        percent$.next(percent);
    }

    private startTimeOut() {
        clearInterval(interval$.getValue() || undefined);
        counter$.next(0);
        percent$.next(0);
        showProgress$.next(true);
        interval$.next(setInterval(() => {
            const _notifier = notifier$.getValue();
            const _timeOut = _notifier?.timeOut || 6_000
            counter$.next(counter$.getValue() + 1);
            percent$.next((counter$.getValue() * 100) / (_timeOut / 200 - 1));
            if (counter$.getValue() >=_timeOut / 200) {
                this.close()
                setTimeout(() => {
                    this.reset()
                }, 500);
                if (!!_notifier.onFinishTimeout) {
                    _notifier.onFinishTimeout();
                }
            }
        }, 200));
    }

    private stopTimer() {
        clearInterval(interval$.getValue() || undefined);
    }

    public onMouseEnter(): void {
        showProgress$.next(false);
        this.setPercent(100);
        this.stopTimer();
    }

    public getShowProgress() {
        return showProgress$.getValue();
    }
}

const instance = new NotifierStore();

/**
 * Instância exposta ao restante da aplicação, que esconde o "close" e "onMouseEnter"
 * pois, não devem ser utilizados externamente.
 */
const notifierStore: INotifierController = instance;
export { notifierStore };

/**
 * Classe que será utilizada pelo componente "Notifier", pois, precisa ter acesso a todos os métodos da classe.
 */
export const notifierDialogController = instance;
